import { useEffect, useState, useRef } from 'react';
import { getDbInstance } from './utils/indexedDB';
import { subscribe, notify } from './utils/subscriptions';
import { debounce } from 'lodash';

// const getDbValue = async <T>(dbName: string, storeName: string, key: string): Promise<T | undefined> => {
//     const db = await getDbInstance(dbName, storeName);
//     return new Promise<T | undefined>((resolve, reject) => {
//       const transaction = db.transaction([storeName], 'readonly');
//       const objectStore = transaction.objectStore(storeName);
//       const request = objectStore.get(key);
  
//       request.onsuccess = (event: Event) => {
//         resolve((event.target as IDBRequest).result ? (event.target as IDBRequest).result.value : undefined);
//       };
  
//       request.onerror = (event: Event) => {
//         console.log("error", key)
//         reject((event.target as IDBRequest).error);
//       };
//     });
// };

// /**
//  * Set the value in IndexedDB immediately without debouncing.
//  */
// const setDbValue = async <T>(dbName: string, storeName: string, key: string, value: T) => {
//   const db = await getDbInstance(dbName, storeName);
//   const transaction = db.transaction([storeName], 'readwrite');
//   const objectStore = transaction.objectStore(storeName);
  
//   objectStore.put({ id: key, value });
//   notify(key, value);
// };

// const setDbValueDebounced = debounce(
//   async <T>(dbName: string, storeName: string, key: string, value: T) => {
//     const db = await getDbInstance(dbName, storeName);
//     const transaction = db.transaction([storeName], 'readwrite');
//     const objectStore = transaction.objectStore(storeName);
    
//     objectStore.put({ id: key, value });
//     notify(key, value);
//   },
//   300
// );

// /**
//  * Custom React hook that persists state in IndexedDB.
//  *
//  * @param {string} key - A unique key to identify the state in IndexedDB.
//  * @param {any} defaultValue - The default value for the state.
//  * @param {string} [dbName='mountq'] - The name of the IndexedDB database where the state will be stored. If not provided, defaults to 'userDatabase'.
//  * @param {string} [storeName='data'] - The name of the object store within the database where the state will be stored. If not provided, defaults to 'userData'.
//  * @return {[any, function]} - An array with two elements:
//  *    - The current state value.
//  *    - A setter function to update the state. This function has the same API as the setter returned by useState.
//  */
// const useDbState = <T>(key:string, defaultValue:any, dbName = 'mountq', storeName = 'data'): [T, React.Dispatch<React.SetStateAction<T>>] => {
//   const [value, setValue] = useState<T>(defaultValue);
//   const [currentDb, setcurrentDb] = useState<string>(dbName);

//   useEffect(() => {
//     const getValue = async () => {
//       try {
//         const storedValue = await getDbValue<T>(dbName, storeName, key);
//         if (storedValue !== undefined) {
//           setValue(storedValue); // Set the stored value if it exists
//         } else {
//           setValue(defaultValue); // Use default value if nothing is stored
//         }
//         setcurrentDb(dbName);
//       } catch (error) {
//         console.error('Error fetching value from IndexedDB:', error);
//       }
//     };

//     getValue();
//   }, [dbName]);

//   useEffect(() => {
//     if (currentDb === dbName) {
//       setDbValue(dbName, storeName, key, value); // Update the IndexedDB whenever the value changes
//     }
//   }, [key, value]); // Persist value to IndexedDB when it changes

//   return [value, setValue];
// };

// export const deleteDatabase = async (dbName: string) => {
//   return new Promise<void>((resolve, reject) => {
//     const request = indexedDB.deleteDatabase(dbName);

//     request.onsuccess = () => {
//       console.log(`Database ${dbName} deleted successfully.`);
//       resolve();
//     };

//     request.onerror = (event: Event) => {
//       console.error(`Failed to delete database ${dbName}:`, event);
//       reject((event.target as IDBRequest).error);
//     };

//     request.onblocked = () => {
//       console.log(`Database deletion is blocked because there are open connections.`);
      
//       // Optionally, you could try delaying the delete and retry if transactions are still open:
//       setTimeout(() => {
//         console.log(`Retrying deletion of ${dbName}...`);
//         deleteDatabase(dbName).then(resolve).catch(reject);
//       }, 5000); // Wait 5 seconds before retrying
//     };
//   });
// };

// export default useDbState;

type DbValue<T> = T | undefined;

const getDbValue = async <T>(
  dbName: string,
  storeName: string,
  key: string
): Promise<DbValue<T>> => {
  const db = await getDbInstance(dbName, storeName);
  return new Promise<DbValue<T>>((resolve, reject) => {
    const tx = db.transaction([storeName], 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.get(key);
    req.onsuccess = () => {
      const result = (req.result as any)?.value;
      resolve(result !== undefined ? result : undefined);
    };
    req.onerror = () => reject(req.error);
  });
};

const writeDb = async <T>(
  dbName: string,
  storeName: string,
  key: string,
  value: T
) => {
  const db = await getDbInstance(dbName, storeName);
  const tx = db.transaction([storeName], 'readwrite');
  tx.objectStore(storeName).put({ id: key, value });
  notify(key, value);
};

const writeDbDebounced = debounce(
  <T>(dbName: string, storeName: string, key: string, value: T) =>
    writeDb(dbName, storeName, key, value),
  300
);

/**
 * Custom hook: loads once, captures initialValue, then only writes when
 * state truly changes away from that initialValue.
 */
function useDbState<T>(
  key: string,
  defaultValue: T,
  dbName = 'mountq',
  storeName = 'data'
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = useState<T>(defaultValue);
  const isLoaded = useRef(false);
  const initialValue = useRef<T>(defaultValue);

  // 1) Load on mount/key/dbName change
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = await getDbValue<T>(dbName, storeName, key);
        if (cancelled) return;
        const next = stored !== undefined ? stored : defaultValue;
        initialValue.current = next; // remember what we started with
        setState(next);
      } catch (err) {
        console.error('Error reading from IndexedDB:', err);
        if (!cancelled) {
          initialValue.current = defaultValue;
          setState(defaultValue);
        }
      } finally {
        if (!cancelled) isLoaded.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, [dbName, storeName, key, defaultValue]);

  // 2) Only write when state changes away from that initialValue
  useEffect(() => {
    if (!isLoaded.current) return;
    // bail if nothing really changed
    if (state === initialValue.current) return;
    // if defaultValue === state, use debounced to avoid too-fast churn
    if (state === defaultValue) {
      writeDbDebounced(dbName, storeName, key, state);
    } else {
      writeDb(dbName, storeName, key, state);
    }
    initialValue.current = state; // update baseline
  }, [state, dbName, storeName, key, defaultValue]);

  return [state, setState];
}

export default useDbState;


export const deleteDatabase = async (dbName: string): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(dbName);

    request.onsuccess = () => {
      console.log(`Database ${dbName} deleted successfully.`);
      resolve();
    };

    request.onerror = (event: Event) => {
      console.error(`Failed to delete database ${dbName}:`, event);
      reject((event.target as IDBRequest).error);
    };

    request.onblocked = () => {
      console.log(`Deletion of ${dbName} is blocked by open connections.`);
      setTimeout(() => {
        deleteDatabase(dbName).then(resolve).catch(reject);
      }, 5000);
    };
  });
};
